import { z } from "zod";

import { api } from "@/api";
import { FullMatchDto } from "./getFullMatches";

// Schema mirrors the backend's bodySchema in sinek-api so the frontend can
// fail-fast on bad payloads before round-tripping to the server.
// Single source of truth (Rule 3): the type is derived from the schema via
// z.infer, not declared separately. Backend re-validates regardless — this
// is defense-in-depth for UX, not a security boundary.

const teamStatsSchema = z.object({
  score: z.number().int().nonnegative().optional(),
  yellowCards: z.number().int().nonnegative().optional(),
  redCards: z.number().int().nonnegative().optional(),
  corners: z.number().int().nonnegative().optional(),
});

const participantsSchema = z.object({
  team1: teamStatsSchema.optional(),
  team2: teamStatsSchema.optional(),
});

const periodStatsSchema = z.object({
  team1: teamStatsSchema,
  team2: teamStatsSchema,
});

const periodSchema = z.object({
  periodNumber: z.number().int().positive(),
  status: z.string().min(1),
  stats: periodStatsSchema.optional(),
});

const baseEventSchema = z.object({
  providerEventId: z.string().min(1),
  minute: z.number().int().nonnegative(),
  seconds: z.number().int().nonnegative(),
  period: z.number().int().positive(),
  participant: z.enum(["team1", "team2"]),
});

const goalEventSchema = baseEventSchema.extend({
  type: z.literal("goal"),
  method: z.literal("penalty").nullable().optional(),
});

const cardEventSchema = baseEventSchema.extend({
  type: z.literal("card"),
  // "yellow_red" = second-yellow dismissal (counts as 1 yellow + 1 red);
  // mirrors the backend enum so such events round-trip without a 400.
  cardType: z.enum(["yellow", "red", "yellow_red"]),
});

const cornerEventSchema = baseEventSchema.extend({
  type: z.literal("corner"),
  // Mirrors the backend CornerSide enum. "unknown" is what the sync stores
  // when a feed (Sportradar, BetGenius play-by-play) gives no side; the form
  // sends stored corners back as they are, so it must round-trip here too.
  side: z.enum(["left", "right", "unknown"]),
});

const penaltyEventSchema = baseEventSchema.extend({
  type: z.literal("penalty"),
});

const eventSchema = z.discriminatedUnion("type", [
  goalEventSchema,
  cardEventSchema,
  cornerEventSchema,
  penaltyEventSchema,
]);

export const manualUpdateMatchPayloadSchema = z.object({
  status: z.string().min(1).optional(),
  currentPeriod: z.number().int().nonnegative().optional(),
  participants: participantsSchema.optional(),
  periods: z.array(periodSchema).optional(),
  events: z.array(eventSchema).optional(),
  managedEventTypes: z
    .array(z.enum(["goal", "card", "corner", "penalty"]))
    .nonempty()
    .optional(),
});

export type ManualUpdateMatchPayload = z.infer<
  typeof manualUpdateMatchPayloadSchema
>;

export const manualUpdateMatch = async (
  matchId: string,
  payload: ManualUpdateMatchPayload
): Promise<FullMatchDto> => {
  const res = await api.client.patch(
    `/matches/${matchId}/manual-update`,
    payload
  );
  return res.data;
};
